/**
 * Stamps the resolved sections cohort onto Datadog RUM + Logs global context.
 * Cohort-level, not per-editor render truth.
 *
 * markdown-native is checked before the predicate because that editor is
 * section-native: its users have sections whatever the vMarkdown-shaped
 * predicate returns. The other arms are read individually rather than through
 * `useSectionsWrappingEnabled`, which collapses them to one boolean and so
 * cannot yield a source. Athena takes no provider argument — passing one pulls
 * in `useIntegrationEhrInfo` and its unconditional `GET /ehr/profile`.
 *
 * Because this mounts at the app shell, the Athena arm's
 * `GET /integration/onboarding-sessions` arms fleet-wide (the determination
 * is backend-authoritative and ungated) — bounded to one deduped request per
 * session via the shared cache entry.
 *
 * Kept out of `useMarkdownSectionalEditorFlags` so editor consumers don't
 * transitively gain a `@datadog/browser-rum` edge.
 */
import { resolveSectionsEnabled } from '@/components/tiptap/vMarkdown/resolveSectionsEnabled';
import {
  useIsAmbientCopilotEnabled,
  useIsMarkdownNativeEditorEnabled,
  useIsSectionsInNotesEnabled,
} from '@/features/flags';
import { useIsMarkdownSectionalEditor } from '@/hooks/editor-version/useResolvedEditorVersion';
import { type SectionsSource, setDatadogSectionsContext } from '@/utils/tracing';
import { useEffect } from 'react';

/**
 * Which arm of the sections predicate put this user in the cohort, or `'off'`.
 */
export const resolveSectionsTelemetryContext = ({
  markdownNativeEnabled,
  flagEnabled,
  athenaEnabled,
  ambientCopilotEnabled,
}: {
  markdownNativeEnabled: boolean;
  flagEnabled: boolean;
  athenaEnabled: boolean;
  ambientCopilotEnabled: boolean;
}): SectionsSource => {
  if (markdownNativeEnabled) return 'markdown_native';

  const enabled = resolveSectionsEnabled({
    enableSections: true,
    enableSectionParser: flagEnabled || athenaEnabled,
    ambientCopilotEnabled,
  });

  if (!enabled) return 'off';
  if (flagEnabled) return 'flag';
  if (athenaEnabled) return 'athena';
  return 'ambient_copilot';
};

export const useSectionsTelemetryContext = (): void => {
  const markdownNativeEnabled = useIsMarkdownNativeEditorEnabled();
  const flagEnabled = useIsSectionsInNotesEnabled();
  const athenaEnabled = useIsMarkdownSectionalEditor();
  const ambientCopilotEnabled = useIsAmbientCopilotEnabled();

  const source = resolveSectionsTelemetryContext({
    markdownNativeEnabled,
    flagEnabled,
    athenaEnabled,
    ambientCopilotEnabled,
  });

  useEffect(() => {
    setDatadogSectionsContext(source !== 'off', source);
  }, [source]);
};
